// Shares callback URL construction and Edge Function response parsing across auth workflow adapters.
import type { VerificationRecoveryResult } from '../core/types';

export interface StructuredEdgeFunctionErrorPayload {
  stage?: string | null;
  message?: string | null;
  code?: string | null;
  details?: Record<string, unknown> | null;
  hint?: string | null;
  retryable?: boolean | null;
  status?: number | null;
}

function hasRecordShape(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function readText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function readDetails(value: unknown): Record<string, unknown> | null {
  if (!hasRecordShape(value)) {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
  } catch {
    return Object.fromEntries(Object.entries(value));
  }
}

export function normalizeStructuredEdgeFunctionErrorPayload(value: unknown): StructuredEdgeFunctionErrorPayload | null {
  if (!hasRecordShape(value)) {
    return null;
  }

  const errorValue = value.error;
  const nestedError = hasRecordShape(errorValue) ? errorValue : null;

  return {
    stage: readText(value.stage) ?? readText(nestedError?.stage) ?? null,
    message:
      readText(value.message) ||
      readText(value.error_description) ||
      readText(nestedError?.message) ||
      readText(nestedError?.error) ||
      null,
    code: readText(value.code) ?? readText(nestedError?.code) ?? null,
    details: readDetails(value.details) ?? readDetails(nestedError?.details) ?? null,
    hint: readText(value.hint) ?? readText(nestedError?.hint) ?? null,
    retryable: readBoolean(value.retryable) ?? readBoolean(nestedError?.retryable) ?? null,
    status: readNumber(value.status) ?? readNumber(nestedError?.status) ?? null,
  };
}

export function readFunctionErrorText(value: unknown): string | null {
  if (typeof value === 'string') {
    return value.trim() || null;
  }

  if (hasRecordShape(value)) {
    const normalized = normalizeStructuredEdgeFunctionErrorPayload(value);
    return normalized?.message ?? null;
  }

  return null;
}

export function buildAuthCallbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

async function parseFunctionErrorMessage(response?: Response): Promise<string | null> {
  if (!response) {
    return null;
  }

  try {
    const payload = await response.clone().json() as Record<string, unknown> | null;
    const normalized = normalizeStructuredEdgeFunctionErrorPayload(payload);
    return normalized?.message || readFunctionErrorText(payload?.error) || readFunctionErrorText(payload) || null;
  } catch {
    try {
      const text = (await response.clone().text()).trim();
      return text || null;
    } catch {
      return null;
    }
  }
}

export async function parseFunctionPayload(response?: Response): Promise<Record<string, unknown> | null> {
  if (!response) {
    return null;
  }

  try {
    return await response.clone().json() as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function pickResponseHeaders(response?: Response): Record<string, string> {
  if (!response) {
    return {};
  }

  const keys = ['content-type', 'x-relay-error', 'x-request-id', 'cf-ray'];
  const entries = keys
    .map((key) => [key, response.headers.get(key)] as const)
    .filter(([, value]) => Boolean(value));

  return Object.fromEntries(entries) as Record<string, string>;
}

export async function extractFunctionError(error: Error, response?: Response): Promise<Error> {
  const message = await parseFunctionErrorMessage(response);
  return new Error(message || error.message);
}

export function normalizeVerificationRecoveryResult(
  data: Partial<VerificationRecoveryResult> | null | undefined,
  email: string,
): VerificationRecoveryResult {
  return {
    error: null,
    email,
    canResend: data?.canResend ?? true,
    cooldownSecondsRemaining: data?.cooldownSecondsRemaining ?? 0,
    nextAllowedAt: data?.nextAllowedAt ?? null,
    lastSentAt: data?.lastSentAt ?? null,
    debug: data?.debug,
    httpStatus: data?.httpStatus ?? null,
    responseHeaders: data?.responseHeaders ?? {},
  };
}
